/**
 * AuthProvider for TENANT users. Manages user + tenant state via /auth/me.
 *
 *   state: null   -> loading (initial check)
 *   state: false  -> not authenticated
 *   state: obj    -> authenticated { user, tenant }
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null); // null | false | {user, tenant}

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setSession(data);
            return data;
        } catch {
            setSession(false);
            return false;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const requestOtp = useCallback(async (email) => {
        const { data } = await api.post("/auth/request-otp", { email });
        return data; // { ok, message, dev_code? }
    }, []);

    const verifyOtp = useCallback(async (email, code) => {
        const { data } = await api.post("/auth/verify-otp", { email, code });
        setSession(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } finally {
            setSession(false);
        }
    }, []);

    return (
        <AuthContext.Provider value={{ session, refresh, requestOtp, verifyOtp, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
